/**
 * PKCE (Proof Key for Code Exchange) Utilities
 * Implements RFC 7636 for secure OAuth 2.0 flows in public clients.
 */

/**
 * Generates a high-entropy random string to use as a code verifier or state.
 */
export function generateRandomString(length: number = 64): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  const values = new Uint32Array(length);
  window.crypto.getRandomValues(values);
  for (let i = 0; i < length; i++) {
    const val = values[i];
    if (val !== undefined) {
      result += charset.charAt(val % charset.length);
    }
  }
  return result;
}

/**
 * Creates a SHA-256 hash of the verifier and encodes it as Base64URL.
 * This is the 'S256' challenge method.
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Helper to encode an array of bytes to a Base64URL string.
 */
function base64UrlEncode(array: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...array));
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Prepares the session storage and returns the full authorization URL.
 */
export async function prepareAuthRequest(
  authEndpoint: string,
  clientId: string,
  redirectUri: string,
//   add offline_access for prodaction
  scope: string = 'openid profile email',
  additionalParams?: Record<string, string>
): Promise<string> {
  // Defensive cleaning of environment variables
  const cleanAuthEndpoint = authEndpoint.trim().replace(/['"]/g, '');
  const cleanClientId = clientId.trim().replace(/['"]/g, '');
  const cleanRedirectUri = redirectUri.trim().replace(/['"]/g, '');

  const state = generateRandomString(32);
  const nonce = generateRandomString(32);
  const codeVerifier = generateRandomString(128);
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  if (!cleanAuthEndpoint) {
    throw new Error('OAuth configuration error: Auth Endpoint is missing. Check your .env file.');
  }

  // Store for verification on return
  sessionStorage.setItem('cial_auth_state', state);
  sessionStorage.setItem('cial_auth_nonce', nonce);
  sessionStorage.setItem('cial_auth_code_verifier', codeVerifier);

  const url = new URL(cleanAuthEndpoint);
  url.searchParams.append('response_type', 'code');
  url.searchParams.append('client_id', cleanClientId);
  url.searchParams.append('redirect_uri', cleanRedirectUri);
  url.searchParams.append('scope', scope);
  url.searchParams.append('state', state);
  url.searchParams.append('nonce', nonce);
  url.searchParams.append('code_challenge', codeChallenge);
  url.searchParams.append('code_challenge_method', 'S256');
  if (additionalParams) {
    for (const [key, value] of Object.entries(additionalParams)) {
      if (!key || value === undefined || value === null || value === '') {
        continue;
      }
      url.searchParams.append(key, value);
    }
  }

  const finalUrl = url.toString();
  return finalUrl;
}
